import { WarLook } from "../war/warLook";
import { GameConstants, Layer, ObjectCategory, ZIndexes } from "@common/constants";
import { type BuildingDefinition, type BuildingImageDefinition } from "@common/definitions/buildings";
import { RoofState, TornRoofs } from "@common/definitions/storm";
import { MaterialSounds, type ObstacleDefinition } from "@common/definitions/obstacles";
import { type Orientation } from "@common/typings";
import { CircleHitbox, GroupHitbox, PolygonHitbox, RectangleHitbox, type Hitbox } from "@common/utils/hitbox";
import { equivLayer, isUpperFloor } from "@common/utils/layer";
import { Angle, EaseFunctions, Numeric } from "@common/utils/math";
import { type ObjectsNetData } from "@common/utils/objectsSerializations";
import { randomBoolean, randomFloat, randomRotation } from "@common/utils/random";
import { Vec, type Vector } from "@common/utils/vector";
import { Container, Graphics } from "pixi.js";
import { Game } from "../game";
import { NormalLook } from "../managers/normalLook";
import { GroundMaterials } from "../war/groundMaterials";
import { CombatFx, particleTint } from "../managers/combatFx";
import { CombatSounds } from "../managers/combatSounds";
import { impactKindOf } from "../managers/combatSoundsLogic";
import { ParticleManager } from "../managers/particleManager";
import { SnowLook } from "../managers/snowLook";
import { SoundManager, type GameSound } from "../managers/soundManager";
import { DIFF_LAYER_HITBOX_OPACITY, HITBOX_COLORS, LAYER_TRANSITION_DELAY, PIXI_SCALE } from "../utils/constants";
import { DebugRenderer } from "../utils/debugRenderer";
import { boundsReach } from "../utils/hitboxBounds";
import { drawGroundGraphics, TrenchioSprite, toPixiCoords } from "../utils/pixi";
import { type Tween } from "../utils/tween";
import { GameObject } from "./gameObject";
import type { BulletStrike } from "./obstacle";
import { GameConsole } from "../console/gameConsole";

export class Building extends GameObject.derive(ObjectCategory.Building) {
    definition!: BuildingDefinition;

    hitbox?: Hitbox;

    graphics?: Graphics;
    /** its flat shapes' normal maps (0.98.7), drawn into the lighting's picture of the shapes (managers/normalLook.ts) */
    normalGraphics?: Graphics;

    ceilingContainer = new Container({ sortableChildren: true });
    ceilingHitbox?: Hitbox;
    ceilingTween?: Tween<Container>;

    orientation!: Orientation;

    ceilingVisible = false;

    puzzle: ObjectsNetData[ObjectCategory.Building]["puzzle"];

    sound?: GameSound;

    particleFrames!: string[];

    hitSound?: GameSound;

    maskHitbox?: GroupHitbox<RectangleHitbox[]>;

    mask?: Graphics;

    images = new Map<BuildingImageDefinition, {
        isCeiling: boolean
        sprite: TrenchioSprite
    }>();

    /** a roof's deck (`openAir`, 1.08): where one stands on it (its floors), placed */
    deckFloor?: Hitbox;

    /** its roof's panes smashed (`BuildingDefinition.glass`, 1.08), a bit each, as the server sent them */
    brokenGlass = 0;

    /** its roof (0.98.4, `RoofState`): whole, or torn off by a tornado (drawn as what's left of it: `TornRoofs`) */
    roof: RoofState = RoofState.Whole;

    /** the smashed panes' pictures, by pane: with its roof (a deck's on its floor) */
    private readonly _brokenPanes = new Map<number, TrenchioSprite>();

    constructor(id: number, data: ObjectsNetData[ObjectCategory.Building]) {
        super(id);

        this.container.sortableChildren = true;
        this.containers.push(this.ceilingContainer);

        this.updateFromData(data, true);
    }

    ceilingRaycastLines?: [Vector, Vector][];

    /**
     * what could stop a roof's ray cast from `start` no further than `reach` (game units): the obstacles, then the
     * buildings, the page knows (in that order, as they were looked through) that a ray stops at (standing, or a
     * building with a damaged ceiling; no window; on the player's floor; not walked through), whose hitbox reaches
     * into the rays' reach (0.98.8)
     */
    private _rayBlockers(player: { readonly layer: Layer }, start: Vector, reach: number): Array<{ readonly hitbox?: Hitbox }> {
        const out: Array<{ readonly hitbox?: Hitbox }> = [];
        const minX = start.x - reach, minY = start.y - reach, maxX = start.x + reach, maxY = start.y + reach;
        const look = (object: { readonly damageable?: boolean, readonly dead: boolean, readonly isBuilding?: boolean, readonly isObstacle?: boolean, readonly definition: ObstacleDefinition | BuildingDefinition, readonly layer: Layer, readonly hitbox?: Hitbox }): void => {
            const { damageable, dead, isBuilding, definition, layer, hitbox, isObstacle } = object;
            if (
                !damageable
                || (dead && !(isBuilding && (definition as BuildingDefinition).hasDamagedCeiling))
                || ("isWindow" in definition && definition.isWindow)
                || !equivLayer({ layer, definition }, player)
                || (isObstacle && (definition as ObstacleDefinition).noCollisions)
                || !hitbox
                || !boundsReach(hitbox, minX, minY, maxX, maxY)
            ) return;
            out.push(object);
        };
        for (const obstacle of Game.objects.getCategory(ObjectCategory.Obstacle)) look(obstacle);
        for (const building of Game.objects.getCategory(ObjectCategory.Building)) look(building);
        return out;
    }

    toggleCeiling(): void {
        if (this.ceilingRaycastLines) this.ceilingRaycastLines.length = 0;

        if (this.ceilingHitbox === undefined || this.ceilingTween || (this.dead && !this.definition.hasDamagedCeiling)) return;
        const player = Game.activePlayer;
        if (player === undefined) return;

        let visible = true;

        if (GameConsole.getBuiltInCVar("db_hide_ceilings")) {
            visible = false;
        } else if (player.air) {
            // in the air (1.06, jumping in): over the roof, never under it
            visible = true;
        } else if (Game.onDeck && player.layer > this.layer) {
            // on a roof's deck (1.08): the roofs under it stay, its own building's and every other's (one stands over
            // them, never inside)
            visible = true;
        } else if (isUpperFloor(this.layer) && (!Game.hideSecondFloor || player.layer < this.layer - 1)) {
            // an upper floor's roof stays while the player is outside (its floors are all shown, the top one's roof
            // over the rest) and while they are on a floor below it (its floor is hidden then: see CameraManager)
            visible = true;
        } else if (this.ceilingHitbox.collidesWith(player.hitbox)) {
            // If the player is inside the ceiling hitbox, we know the ceiling should be hidden
            visible = false;
        } else {
            // Otherwise, we do some raycasting to check
            const visionSize = GameConstants.player.buildingVisionSize;
            const rayStart = player.position;
            const playerHitbox = new CircleHitbox(visionSize, rayStart);
            const halfPi = Math.PI / 2;
            // (0.98.8: what could stop a ray, gathered once, and only when a ray is cast (a roof part within sight):
            // every obstacle and building the page knows used to be copied into a list for every building at every
            // update, and each ray tested them all. The same tests, those that don't depend on the ray done once, and
            // only what reaches into the rays' reach, `visionSize` round the player: a ray can't cross what doesn't)
            let blockers: ReadonlyArray<{ readonly hitbox?: Hitbox }> | undefined;
            const potentials = (): ReadonlyArray<{ readonly hitbox?: Hitbox }> => blockers ??= this._rayBlockers(player, rayStart, visionSize);

            const hitboxes = this.ceilingHitbox instanceof GroupHitbox ? this.ceilingHitbox.hitboxes : [this.ceilingHitbox];
            for (const hitbox of hitboxes) {
                // TODO
                if (hitbox instanceof PolygonHitbox) continue;

                const direction = playerHitbox.getIntersection(hitbox)?.dir;
                if (!direction) continue;

                const angle = Math.atan2(direction.y, direction.x);

                let raysPenetrated = 0;
                for (let i = angle - halfPi; i < angle + halfPi; i += 0.1) {
                    const rayEnd = Vec.add(rayStart, Vec.fromPolar(i, visionSize));
                    const rayCeilingIntersection = this.ceilingHitbox.intersectsLine(rayStart, rayEnd)?.point;

                    if (DEBUG_CLIENT) {
                        (this.ceilingRaycastLines ??= []).push([rayStart, rayCeilingIntersection ?? rayEnd]);
                    }

                    if (!rayCeilingIntersection) continue;

                    let collided = false;
                    for (const { hitbox } of potentials()) {
                        if (!hitbox?.intersectsLine(rayStart, rayCeilingIntersection)) continue;

                        collided = true;
                        break;
                    }
                    if (!collided) raysPenetrated++;

                    if (raysPenetrated > 1) break;
                }
                visible = raysPenetrated <= 1;

                if (!visible) break;
            }
        }

        const alpha = visible ? 1 : this.definition.ceilingHiddenAlpha ?? 0;

        this.ceilingVisible = visible;

        if (this.ceilingContainer.alpha === alpha || this.ceilingTween) return;

        this.ceilingTween = Game.addTween({
            target: this.ceilingContainer,
            to: { alpha },
            duration: LAYER_TRANSITION_DELAY,
            ease: EaseFunctions.sineOut,
            onComplete: () => {
                this.ceilingTween = undefined;
            }
        });
    }

    override updateFromData(data: ObjectsNetData[ObjectCategory.Building], isNew = false): void {
        if (data.full) {
            const full = data.full;
            const definition = this.definition = full.definition;
            this.position = full.position;

            // If there are multiple particle variations, generate a list of variation image names
            const particleImage = definition.particle ?? `${definition.idString}_particle`;

            this.particleFrames = definition.particleVariations !== undefined
                ? Array.from({ length: definition.particleVariations }, (_, i) => `${particleImage}_${i + 1}`)
                : [particleImage];

            this.layer = data.layer;
            const pos = toPixiCoords(this.position);
            this.container.position.copyFrom(pos);
            this.ceilingContainer.position.copyFrom(pos);

            this.orientation = full.orientation;
            this.rotation = Angle.orientationToRotation(this.orientation);
            this.container.rotation = this.rotation;
            this.ceilingContainer.rotation = this.rotation;

            if (definition.graphics?.length) {
                this.graphics = new Graphics();
                this.graphics.zIndex = definition.graphicsZIndex ?? ZIndexes.BuildingsFloor;
                // (0.98.7: each shape in its material, a road's asphalt, a lot's concrete, tinted to its colour; and
                // its normal map's twin for the lighting's picture of the shapes: war/groundMaterials.ts)
                const twin = new Graphics();
                let textured = false;
                for (const graphics of definition.graphics) {
                    const hitbox = graphics.hitbox.transform(this.position, 1, this.orientation);
                    if (GroundMaterials.materialFor(graphics.color, hitbox)) textured = true;
                    GroundMaterials.fill(this.graphics, hitbox, graphics.color);
                    GroundMaterials.fill(twin, hitbox, graphics.color, PIXI_SCALE, true);
                }
                this.containers.push(this.graphics);
                if (textured) {
                    this.normalGraphics = twin;
                    NormalLook.addTwin(twin, () => this.layerContainer);
                } else twin.destroy();
            }

            for (const override of definition.visibilityOverrides ?? []) {
                this.maskHitbox ??= new GroupHitbox();

                const collider: Hitbox = override.collider.transform(this.position, 1, this.orientation);
                if (collider instanceof RectangleHitbox) {
                    this.maskHitbox.hitboxes.push(collider);
                } else if (collider instanceof GroupHitbox) {
                    for (const hitbox of (collider).hitboxes) {
                        this.maskHitbox.hitboxes.push(hitbox as RectangleHitbox);
                    }
                }
            }

            if (definition.bulletMask) {
                (this.maskHitbox ??= new GroupHitbox()).hitboxes.push(definition.bulletMask.transform(this.position, 1, this.orientation));
            }

            if (this.maskHitbox) {
                this.mask = new Graphics();
                this.mask.alpha = 0;
                this.containers.push(this.mask);

                for (const hitbox of this.maskHitbox.hitboxes) {
                    const { min, max } = hitbox;
                    this.mask
                        .beginPath()
                        .rect(
                            min.x * PIXI_SCALE,
                            min.y * PIXI_SCALE,
                            (max.x - min.x) * PIXI_SCALE,
                            (max.y - min.y) * PIXI_SCALE
                        )
                        .closePath()
                        .fill(0xffffff);
                }
            }

            this.updateLayer();

            this.hitbox = definition.hitbox?.transform(this.position, 1, this.orientation);
            this.damageable = !!definition.hitbox;
            this.ceilingHitbox = definition.ceilingHitbox?.transform(this.position, 1, this.orientation);
            if (definition.openAir && definition.floors?.length) {
                const floors = definition.floors.map(({ hitbox }) => hitbox.transform(this.position, 1, this.orientation));
                this.deckFloor = floors.length === 1
                    ? floors[0]
                    : new GroupHitbox(...floors.flatMap(floor => floor instanceof GroupHitbox ? floor.hitboxes : [floor as RectangleHitbox]));
            }
        }

        const definition = this.definition;

        if (definition === undefined) {
            console.warn("Building partially updated before being fully updated");
        }

        if (definition.sounds) {
            const { sounds } = definition;
            const soundOptions = {
                position: Vec.add(Vec.rotate(sounds.position ?? Vec(0, 0), this.rotation), this.position),
                layer: this.layer,
                fallOff: sounds.falloff,
                maxRange: sounds.maxRange,
                dynamic: true,
                ambient: true,
                loop: true
            };

            if (
                sounds.normal !== undefined
                && !data.puzzle?.solved
                && this.sound?.name !== sounds.normal
            ) {
                this.sound?.stop();
                this.sound = SoundManager.play(sounds.normal, soundOptions);
            }

            if (
                sounds.solved !== undefined
                && data.puzzle?.solved
                && this.sound?.name !== sounds.solved
            ) {
                this.sound?.stop();
                if (sounds.solved !== null) this.sound = SoundManager.play(sounds.solved, soundOptions);
            }
        }

        if (data.dead) {
            if (!this.dead && !isNew && !definition.noCeilingCollapseEffect) {
                let particleFrame = definition.ceilingCollapseParticle ?? `${definition.idString}_particle`;

                if (definition.ceilingCollapseParticleVariations) {
                    particleFrame += `_${Math.floor(Math.random() * definition.ceilingCollapseParticleVariations) + 1}`;
                }

                ParticleManager.spawnParticles(10, () => ({
                    frames: particleFrame,
                    position: this.ceilingHitbox?.randomPoint() ?? { x: 0, y: 0 },
                    zIndex: Numeric.max(ZIndexes.Players + 1, 4),
                    layer: this.layer,
                    lifetime: 2000,
                    rotation: {
                        start: randomRotation(),
                        end: randomRotation()
                    },
                    alpha: {
                        start: 1,
                        end: 0,
                        ease: EaseFunctions.sexticIn
                    },
                    scale: { start: (definition.ceilingCollapseParticle ? 2 : 1), end: 0.2 },
                    speed: Vec.fromPolar(randomRotation(), randomFloat(1, 2))
                }));

                this.playSound(
                    definition.ceilingCollapseSound ?? "ceiling_collapse",
                    {
                        falloff: 0.5,
                        maxRange: 96
                    }
                );
            }
            this.ceilingTween?.kill();
            this.ceilingContainer.alpha = 1;
        }
        this.dead = data.dead;

        if (data.puzzle) {
            if (!isNew && data.puzzle.errorSeq !== this.puzzle?.errorSeq) {
                this.playSound("puzzle_error");
            }

            if (!isNew && data.puzzle.solved) {
                if (definition.puzzle?.solvedSound) {
                    SoundManager.play("puzzle_solved", {
                        position: definition.puzzle.soundPosition
                            ? Vec.addAdjust(this.position, definition.puzzle.soundPosition, this.orientation)
                            : this.position,
                        layer: this.layer
                    });
                }
            }
        }
        this.puzzle = data.puzzle;

        this._updateRoof(data.roof ?? RoofState.Whole, isNew);
        this._createSprites();
        this._updateGlass(data.brokenGlass, isNew);

        this.toggleCeiling();

        this.ceilingContainer.zIndex = (this.dead && !this.definition.hasDamagedCeiling)
            ? ZIndexes.DeadObstacles
            : this.definition.ceilingZIndex ?? ZIndexes.BuildingsCeiling;
        this.container.zIndex = this.definition.floorZIndex ?? ZIndexes.BuildingsFloor;
    }

    override updateDebugGraphics(): void {
        if (!DEBUG_CLIENT) return;

        const definition = this.definition;
        const alpha = this.layer === Game.layer ? 1 : DIFF_LAYER_HITBOX_OPACITY;

        if (GameConsole.getBuiltInCVar("db_show_hitboxes_buildings_ceilings")) {
            for (const [start, end] of this.ceilingRaycastLines ?? []) {
                DebugRenderer.addLine(
                    start,
                    end,
                    HITBOX_COLORS.buildingCeilingRaycast,
                    alpha
                );
            }

            if (definition.ceilingHitbox) {
                DebugRenderer.addHitbox(
                    definition.ceilingHitbox.transform(this.position, 1, this.orientation),
                    definition.noCeilingScopeEffect ? HITBOX_COLORS.buildingScopeCeiling : HITBOX_COLORS.buildingZoomCeiling,
                    alpha
                );
            }
        }

        if (!GameConsole.getBuiltInCVar("db_show_hitboxes_buildings")) return;

        if (this.hitbox) {
            DebugRenderer.addHitbox(
                this.hitbox,
                HITBOX_COLORS.obstacle,
                alpha
            );
        }

        DebugRenderer.addHitbox(
            definition.spawnHitbox.transform(this.position, 1, this.orientation),
            HITBOX_COLORS.spawnHitbox,
            alpha
        );

        if (definition.bulletMask) {
            DebugRenderer.addHitbox(
                definition.bulletMask.transform(this.position, 1, this.orientation),
                HITBOX_COLORS.bulletMask,
                alpha
            );
        }

        if (definition.bridgeHitbox) {
            DebugRenderer.addHitbox(
                definition.bridgeHitbox.transform(this.position, 1, this.orientation),
                HITBOX_COLORS.landHitbox,
                alpha
            );
        }

        if (definition.bunkerSpawnHitbox) {
            DebugRenderer.addHitbox(
                definition.bunkerSpawnHitbox.transform(this.position, 1, this.orientation),
                HITBOX_COLORS.bunkerSpawnHitbox,
                Game.layer === Layer.Basement ? 1 : DIFF_LAYER_HITBOX_OPACITY
            );
        }

        for (const { collider, layer } of definition.visibilityOverrides ?? []) {
            DebugRenderer.addHitbox(
                collider.transform(this.position, 1, this.orientation),
                HITBOX_COLORS.buildingVisOverride,
                layer === Game.layer ? 1 : DIFF_LAYER_HITBOX_OPACITY
            );
        }
    }

    private _createSprites(): void {
        const { ceilingImages = [], floorImages = [] } = this.definition;

        for (const image of ceilingImages) {
            this._updateImage(image, true);
        }
        for (const image of floorImages) {
            this._updateImage(image, false);
        }

        // delete sprites not in the current definition
        const definitionSprites = new Set([...ceilingImages, ...floorImages]);
        for (const [definition, image] of this.images) {
            if (definitionSprites.has(definition)) continue;
            image.sprite.destroy();
            this.images.delete(definition);
        }
    }

    private _updateImage(imageDef: BuildingImageDefinition, isCeiling: boolean): void {
        const isNewSprite = !this.images.has(imageDef);

        const image = this.images.get(imageDef) ?? {
            sprite: new TrenchioSprite(),
            definition: imageDef,
            isCeiling
        };

        this.images.set(imageDef, image);

        if (imageDef.beachTinted) {
            image.sprite.setTint(Game.colors.beach);
        }
        // battlefield look: Fargo's bright sand and asphalt ground tiles are dirtied down to sit in the mud
        if (!isCeiling && WarLook.active && /^(sand_ground|road)/.test(this.definition.idString)) {
            image.sprite.setTint(this.definition.idString.startsWith("road") ? 0x8a8373 : 0x6c6050);
        }

        const { sprite } = image;

        if (isNewSprite) {
            if (isCeiling) this.ceilingContainer.addChild(sprite);
            else this.container.addChild(sprite);
        }

        // (0.98.4: a roof torn off by a tornado is drawn as what's left of it, its rooms open to the sky: the first of
        // its ceiling's pictures the torn one, any other gone with the roof)
        const torn = isCeiling && this.roof === RoofState.Torn ? TornRoofs[this.definition.idString] : undefined;
        const first = this.definition.ceilingImages?.[0] === imageDef;
        const frame = torn !== undefined
            ? (first ? torn : undefined)
            : this.dead
                ? imageDef.residue ?? imageDef.damaged ?? imageDef.key
                : imageDef.key;

        sprite
            .setZIndex(imageDef.zIndex ?? ZIndexes.Ground) // broken
            .setVPos(toPixiCoords(imageDef.position))
            .setVisible(frame !== undefined && !(this.dead && imageDef.hideOnDead) && !(!this.dead && imageDef.hideOnAlive));
        if (frame !== undefined) sprite.setFrame(frame);
        // (0.98.5: snow on the roof in the snow; none on what's left of a torn one, nor on a ruin's)
        if (isCeiling && SnowLook.active) {
            // (none on a roof drawn in a colour of its own, a tent's, a container's: its cap would take the colour)
            if (frame !== undefined && torn === undefined && !this.dead && imageDef.tint === undefined) SnowLook.cap(sprite, frame, "roof");
            else SnowLook.uncap(sprite);
        }

        if (imageDef.spinSpeed !== undefined ? isNewSprite : true) {
            sprite.setRotation(imageDef.rotation ?? 0);
        }

        if (imageDef.residuePosition !== undefined && this.dead) {
            sprite.setVPos(toPixiCoords(imageDef.residuePosition));
        }

        if (imageDef.alpha) sprite.setAlpha(imageDef.alpha);

        if (imageDef.scale) sprite.scale = (this.definition.resetCeilingResidueScale && this.dead) ? 1 : imageDef.scale;

        if (imageDef.tint !== undefined) sprite.setTint(imageDef.tint);
    }

    /**
     * its roof torn off (0.98.4, a tornado over it: `RoofState.Torn`): drawn as what's left of it from now on
     * (`_updateImage`); torn just now (not a building the page has just been sent), the roof goes up in a whirl of
     * tiles and planks with the crack of its rafters
     */
    private _updateRoof(roof: RoofState, isNew: boolean): void {
        const was = this.roof;
        this.roof = roof;
        if (roof !== RoofState.Torn || was === RoofState.Torn || isNew) return;
        const hitbox = this.ceilingHitbox;
        if (!hitbox) return;
        SoundManager.play("roof_tear", { position: this.position, layer: this.layer, falloff: 0.4, maxRange: 160 });
        for (let i = 0; i < 34; i++) {
            const spot = hitbox.randomPoint();
            const angle = Math.random() * Math.PI * 2;
            ParticleManager.spawnParticle({
                frames: i % 3 === 0 ? "debris_plank" : i % 3 === 1 ? "debris_tile_1" : "debris_tile_2",
                position: spot,
                zIndex: ZIndexes.BuildingsCeiling + 0.9,
                layer: this.layer,
                lifetime: 1600 + Math.random() * 1200,
                scale: { start: 1.1, end: 0.3 },
                alpha: { start: 1, end: 0, ease: EaseFunctions.sexticIn },
                rotation: { start: angle, end: angle + (Math.random() - 0.5) * 14 },
                speed: Vec.fromPolar(angle, 10 + Math.random() * 22)
            });
        }
        ParticleManager.spawnParticles(12, () => ({
            frames: "smoke_particle",
            position: hitbox.randomPoint(),
            zIndex: ZIndexes.BuildingsCeiling + 0.85,
            layer: this.layer,
            lifetime: 1800 + Math.random() * 900,
            scale: { start: 1, end: 3 },
            alpha: { start: 0.4, end: 0 },
            rotation: { start: Math.random() * 6, end: Math.random() * 6 },
            tint: 0x6e6256,
            speed: Vec.fromPolar(Math.random() * Math.PI * 2, 4 + Math.random() * 6)
        }));
    }

    /**
     * its roof's smashed panes drawn (1.08: `BuildingDefinition.glass`), over its roof's picture: in its ceiling's
     * container, or a deck's on its floor; one smashed just now (not a building the page has just been sent) shatters
     * with the glass's sound and its shards
     */
    private _updateGlass(brokenGlass: number, isNew: boolean): void {
        const panes = this.definition.glass ?? [];
        for (let index = 0; index < panes.length; index++) {
            if ((brokenGlass & (1 << index)) === 0 || this._brokenPanes.has(index)) continue;
            const pane = panes[index];
            const sprite = new TrenchioSprite(pane.broken)
                .setVPos(toPixiCoords(pane.position))
                .setRotation(pane.rotation ?? 0)
                .setZIndex(ZIndexes.BuildingsFloor);
            if (pane.scale !== undefined) sprite.scale.set(pane.scale.x, pane.scale.y);
            (this.definition.openAir ? this.container : this.ceilingContainer).addChild(sprite);
            this._brokenPanes.set(index, sprite);
            if (isNew) continue;

            const where = Vec.addAdjust(this.position, pane.position, this.orientation);
            SoundManager.play("glass_destroyed", { position: where, layer: this.layer, falloff: 0.4, maxRange: 96 });
            // (the shards over the roof: seen from outside and from the deck, where the pane is; 1.09's audit)
            ParticleManager.spawnParticles(10, () => ({
                frames: "window_particle",
                position: Vec.add(where, Vec.fromPolar(randomRotation(), randomFloat(0, 1.5))),
                zIndex: ZIndexes.ObstaclesLayer5,
                layer: this.layer,
                lifetime: 1200,
                scale: { start: randomFloat(0.6, 1), end: 0.2 },
                alpha: { start: 1, end: 0, ease: EaseFunctions.sexticIn },
                rotation: { start: randomRotation(), end: randomRotation() },
                speed: Vec.fromPolar(randomRotation(), randomFloat(3, 7))
            }));
        }
        this.brokenGlass = brokenGlass;
    }

    override update(): void {
        for (const [definition, image] of this.images) {
            if (definition.spinSpeed && (definition.spinOnSolve ? this.puzzle?.solved : true)) {
                image.sprite.rotation += definition.spinSpeed;
            }
        }
    }

    override updateInterpolation(): void { /* bleh */ }

    /** something struck a wall (`angle`: its outward angle there); `strike`: it was a bullet */
    hitEffect(position: Vector, angle: number, strike?: BulletStrike): void {
        ParticleManager.spawnParticle({
            frames: this.particleFrames,
            position,
            zIndex: ZIndexes.Players + 1,
            layer: this.layer,
            lifetime: 600,
            scale: { start: 0.9, end: 0.2 },
            alpha: { start: 1, end: 0.65 },
            speed: Vec.fromPolar((angle + randomFloat(-0.3, 0.3)), randomFloat(2.5, 4.5))
        });

        const { material } = this.definition;
        if (!material) return;
        // a bullet's own impact on the wall (managers/combatSounds.ts), and what flies off it (combatFx.ts)
        const kind = strike ? impactKindOf(material) : undefined;
        if (kind !== undefined && strike) {
            CombatFx.impact(position, angle, kind, this.layer, strike.direction, strike.damage, particleTint(this.particleFrames));
            if (CombatSounds.impact(kind, this.id, position, this.layer, strike.glanced)) return;
        }
        this.hitSound?.stop();
        this.hitSound = SoundManager.play(
            `${MaterialSounds[material]?.hit ?? material}_hit_${randomBoolean() ? "1" : "2"}`,
            {
                position,
                falloff: 0.2,
                maxRange: 96,
                layer: this.layer
            }
        );
    }

    override destroy(): void {
        super.destroy();

        this.graphics?.destroy();
        if (this.normalGraphics) {
            NormalLook.removeTwin(this.normalGraphics);
            this.normalGraphics.destroy();
            this.normalGraphics = undefined;
        }
        this.mask?.destroy();
        this.ceilingTween?.kill();
        this.sound?.stop();

        for (const [, image] of this.images) {
            image.sprite.destroy();
        }
        // (its roof's container too, with the smashed panes' pictures on it: a building out of view and back drew its
        // old holes over the new roof, and left a container behind each time; 1.09's audit)
        this.ceilingContainer.destroy({ children: true });
    }
}
